import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Map } from 'immutable';
import { Table, TableContainer, TableHead, TableRow, TableCell, TableBody, TextField, Paper, InputAdornment, MenuItem } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import variables from 'src/style/variable/variables.module.scss';
import { useTranslation } from 'src/i18n';
import { pairUp } from '../Commissioning/utils';
import DeviceTagAutocompleteField from '../Commissioning/DeviceTagAutocompleteField';

// Field lists define the order and set of fields rendered in each info section. They are
// hardcoded because the cable pre-insulation form fields are fixed by product design.
const INFO_FIELDS = [
  'heatTraceCircuit', 'heatTraceCableTag',
  'area', 'system',
  'cableType', 'location',
  'lineNo',
];

// Per-field translation keys. Generic labels reuse existing keys (top-level / commissioningCircuit)
// rather than duplicating them under this form's namespace; form-specific labels keep their own key.
const INFO_LABEL_KEYS: Record<string, string> = {
  heatTraceCircuit: 'commissioningCablePreInsulation.table.heatTraceCircuit',
  heatTraceCableTag: 'commissioningCablePreInsulation.table.heatTraceCableTag',
  area: 'commissioningCircuit.table.area',
  system: 'system',
  cableType: 'commissioningCircuit.table.cableType',
  location: 'location',
  lineNo: 'commissioningCablePreInsulation.table.lineNo',
};

// Measurement fields and the unit adornment each carries (empty string = no unit).
const MEASUREMENT_FIELDS = [
  'cableResistance', 'srCableLength',
  'srCapacitance', 'meggerTestVoltage',
  'meggerInsulationResistance',
];

const MEASUREMENT_UNITS: Record<string, string> = {
  cableResistance: 'Ω',
  srCapacitance: 'nF',
  meggerTestVoltage: 'VDC',
  meggerInsulationResistance: 'MΩ',
};

const MEGGER_TEST_VOLTAGE_OPTIONS = ['500', '1000', '2500'];

export default function CablePreInsulationInfo (props) {
  const { data, onChange, readOnly, linkedDeviceTag, onUnlink, onDeviceSelect } = props;
  const { t } = useTranslation();
  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);

  const [info, setInfo] = useState(Map(data?.get('info') || {}));
  const [measurements, setMeasurements] = useState(Map(data?.get('measurements') || {}));

  useEffect(() => {
    setInfo(Map(data?.get('info') || {}));
    setMeasurements(Map(data?.get('measurements') || {}));
  }, [data]);

  const onInfoChange = (key, val) => {
    setInfo(info.set(key, val));
    onChange(['extras', 'info', key], val);
  };

  const onMeasurementChange = (key, val) => {
    setMeasurements(measurements.set(key, val));
    onChange(['extras', 'measurements', key], val);
  };

  const renderInfoField = (field) => {
    if (field === 'heatTraceCircuit') {
      return (
        <DeviceTagAutocompleteField
          value={info.get(field) || ''}
          disabled={readOnly}
          deviceType='circuit'
          placeholder={t('commissioningCablePreInsulation.table.heatTraceCircuit')}
          linkedDeviceTag={linkedDeviceTag}
          onChange={(val) => onInfoChange(field, val)}
          onDeviceSelect={onDeviceSelect}
          onUnlink={onUnlink}
        />
      );
    }
    return (
      <TextField
        fullWidth
        disabled={readOnly}
        value={info.get(field) || ''}
        onChange={(e) => onInfoChange(field, e.target.value)}
      />
    );
  };

  const renderMeasurementField = (field) => {
    const unit = MEASUREMENT_UNITS[field];
    if (field === 'meggerTestVoltage') {
      return (
        <TextField
          select
          fullWidth
          disabled={readOnly}
          value={measurements.get(field) || ''}
          onChange={(e) => onMeasurementChange(field, e.target.value)}
          InputProps={{ endAdornment: <InputAdornment position='end'>{unit}</InputAdornment> }}
        >
          <MenuItem value=''>—</MenuItem>
          {MEGGER_TEST_VOLTAGE_OPTIONS.map((opt) => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </TextField>
      );
    }
    return (
      <TextField
        fullWidth
        disabled={readOnly}
        value={measurements.get(field) || ''}
        onChange={(e) => onMeasurementChange(field, e.target.value)}
        InputProps={unit ? { endAdornment: <InputAdornment position='end'>{unit}</InputAdornment> } : undefined}
      />
    );
  };

  const infoPairs = pairUp(INFO_FIELDS);
  const measurementPairs = pairUp(MEASUREMENT_FIELDS);

  const renderPairedTable = (pairs, labelFor, renderField) => (
    <TableContainer className={`default-control-panel-table${isMobile ? ' mobile-table' : ''}`}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell className='sub-headers'>{t('common.field')}</TableCell>
            <TableCell className='sub-headers'>{t('common.value')}</TableCell>
            <TableCell className='sub-headers'>{t('common.field')}</TableCell>
            <TableCell className='sub-headers'>{t('common.value')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pairs.map(([fieldA, fieldB]) => (
            <TableRow key={fieldA}>
              <TableCell className='sub-headers'>{t(labelFor(fieldA))}</TableCell>
              <TableCell className='body-cell'>{renderField(fieldA)}</TableCell>
              {fieldB ? (
                <>
                  <TableCell className='sub-headers'>{t(labelFor(fieldB))}</TableCell>
                  <TableCell className='body-cell'>{renderField(fieldB)}</TableCell>
                </>
              ) : (
                <>
                  <TableCell />
                  <TableCell />
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <div>
      <Paper sx={{ marginBottom: '30px' }}>
        <h3>{t('commissioningCablePreInsulation.modalTitle')}</h3>
        {renderPairedTable(infoPairs, (field) => INFO_LABEL_KEYS[field], renderInfoField)}
      </Paper>

      <Paper sx={{ marginBottom: '30px' }}>
        <h3>{t('commissioningCablePreInsulation.measurements.title')}</h3>
        {renderPairedTable(measurementPairs, (field) => `commissioningCablePreInsulation.measurements.${field}`, renderMeasurementField)}
      </Paper>
    </div>
  );
}

CablePreInsulationInfo.propTypes = {
  data: ImmutablePropTypes.map,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  linkedDeviceTag: PropTypes.string,
  onUnlink: PropTypes.func,
  onDeviceSelect: PropTypes.func,
};

CablePreInsulationInfo.defaultProps = {
  readOnly: false,
  linkedDeviceTag: null,
  onUnlink: () => {},
  onDeviceSelect: null,
};
